// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, AlertTriangle, Pencil, Check, X } from 'lucide-react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import '../../styles/floor-selector.css';
import { toast } from 'react-toastify';

interface Floor {
  id: string;
  name: string;
  width: number;
  height: number;
  texture: string;
  tables: any[];
  elements: any[];
}

interface FloorSelectorProps {
  floors: Floor[];
  currentFloor: string;
  onChange: (floorId: string) => void;
  isCollapsed?: boolean;
  isFocused?: boolean;
  startAddingWhenEmpty?: boolean;
  onToggle?: () => void;
}

const FloorSelector: React.FC<FloorSelectorProps> = ({
  floors,
  currentFloor,
  onChange,
  isCollapsed = false,
  isFocused = false,
  startAddingWhenEmpty = false,
  onToggle
}) => {
  const { addFloor, deleteFloor, updateFloor } = useRestaurant();
  const [isAddingFloor, setIsAddingFloor] = useState(false);
  const [newFloorName, setNewFloorName] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const [floorPendingDelete, setFloorPendingDelete] = useState<Floor | null>(null);
  const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
  const [reservationsToDelete, setReservationsToDelete] = useState<number | null>(null);
  const [forceDeleteMessage, setForceDeleteMessage] = useState('');
  const [isForceDeleting, setIsForceDeleting] = useState(false);
  const [editingFloorId, setEditingFloorId] = useState<string | null>(null);
  const [editingFloorName, setEditingFloorName] = useState('');
  const [savingFloorId, setSavingFloorId] = useState<string | null>(null);

  useEffect(() => {
    if (isAddingFloor) {
      // Wait for the form to render then scroll to the end
      requestAnimationFrame(() => {
        listRef.current?.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: 'smooth'
        });
      });
    }
  }, [isAddingFloor]);

  useEffect(() => {
    if (!editingFloorId) return;

    requestAnimationFrame(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    });
  }, [editingFloorId]);

  useEffect(() => {
    if (!editingFloorId) return;

    const floorStillExists = floors.some((floor) => String(floor.id) === String(editingFloorId));
    if (!floorStillExists) {
      setEditingFloorId(null);
      setEditingFloorName('');
      setSavingFloorId(null);
    }
  }, [editingFloorId, floors]);

  useEffect(() => {
    if (startAddingWhenEmpty && floors.length === 0) {
      setIsAddingFloor(true);
    }
  }, [floors.length, startAddingWhenEmpty]);
  
  const handleAddFloor = () => {
    setEditingFloorId(null);
    setEditingFloorName('');
    setIsAddingFloor(true);
  };

  const handleStartEditFloor = (
    event: React.MouseEvent<HTMLButtonElement>,
    floor: Floor
  ) => {
    event.stopPropagation();
    setIsAddingFloor(false);
    setNewFloorName('');
    setEditingFloorId(String(floor.id));
    setEditingFloorName(floor.name || '');
  };

  const handleCancelEditFloor = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (savingFloorId) return;
    setEditingFloorId(null);
    setEditingFloorName('');
  };

  const handleSaveFloorName = async (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();

    if (!editingFloorId) return;

    const trimmedName = editingFloorName.trim();
    if (!trimmedName) {
      toast.warning('Informe um nome para o ambiente.');
      return;
    }

    const currentEditingFloor = floors.find((floor) => String(floor.id) === String(editingFloorId));
    if (!currentEditingFloor) {
      setEditingFloorId(null);
      setEditingFloorName('');
      return;
    }

    if (trimmedName === String(currentEditingFloor.name || '').trim()) {
      setEditingFloorId(null);
      setEditingFloorName('');
      return;
    }

    setSavingFloorId(editingFloorId);

    try {
      await updateFloor(editingFloorId, { name: trimmedName });
      toast.success('Nome do ambiente atualizado.');
      setEditingFloorId(null);
      setEditingFloorName('');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao atualizar o nome do ambiente.';
      toast.error(errorMessage);
    } finally {
      setSavingFloorId(null);
    }
  };

  const handleDeleteFloor = async (
    event: React.MouseEvent<HTMLButtonElement>,
    floor: Floor
  ) => {
    event.stopPropagation();

    try {
      const result = await deleteFloor(floor.id);
      const deletedReservations = result?.deletedReservations ?? 0;
      const successMessage = deletedReservations > 0
        ? `Planta e ${deletedReservations} reserva${deletedReservations === 1 ? '' : 's'} removidas.`
        : 'Planta excluída com sucesso.';
      toast.success(successMessage);
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 409) {
        const reservationsCount = error?.response?.data?.details?.reservationsCount ?? null;
        setFloorPendingDelete(floor);
        setReservationsToDelete(reservationsCount);

        const defaultMessage = 'Existem reservas associadas a esta planta. Você pode excluir todas elas para continuar.';
        const backendMessage = error?.response?.data?.error || defaultMessage;

        const countMessage = reservationsCount && reservationsCount > 0
          ? `Encontramos ${reservationsCount} reserva${reservationsCount === 1 ? '' : 's'} agendadas para "${floor.name}".`
          : backendMessage;

        setForceDeleteMessage(countMessage);
        setShowForceDeleteModal(true);
        return;
      }

      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao remover sala.';
      toast.error(errorMessage);
    }
  };

  const handleConfirmForceDelete = async () => {
    if (!floorPendingDelete) return;

    setIsForceDeleting(true);

    try {
      const result = await deleteFloor(floorPendingDelete.id, { force: true });
      const deletedReservations = result?.deletedReservations ?? reservationsToDelete ?? 0;
      const successMessage = deletedReservations > 0
        ? `Planta excluída. ${deletedReservations} reserva${deletedReservations === 1 ? '' : 's'} foram removidas.`
        : 'Planta excluída com sucesso.';
      toast.success(successMessage);
      setShowForceDeleteModal(false);
      setFloorPendingDelete(null);
      setReservationsToDelete(null);
      setForceDeleteMessage('');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao remover sala.';
      toast.error(errorMessage);
    } finally {
      setIsForceDeleting(false);
    }
  };

  const handleCancelForceDelete = () => {
    setShowForceDeleteModal(false);
    setFloorPendingDelete(null);
    setReservationsToDelete(null);
    setForceDeleteMessage('');
  };

  const handleSaveNewFloor = async () => {
    if (!newFloorName.trim()) return;

    const newFloorId = await addFloor({
      name: newFloorName.trim(),
      width: 800,
      height: 600,
      texture: 'wood',
      modo_layout: false,
      tables: [],
      elements: []
    });
    onChange(newFloorId);
    setIsAddingFloor(false);
    setNewFloorName('');
  };
  
  const handleCancelNewFloor = () => {
    setIsAddingFloor(false);
    setNewFloorName('');
  };
  
  return (
    <div className={`floor-selector ${isCollapsed ? 'collapsed' : ''} ${isFocused ? 'focused' : ''}`}>
      <div className="selector-header" onClick={onToggle}>
        <h3>Ambientes</h3>
        <div className="header-actions">
          <button
            className="add-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleAddFloor();
            }}
          >
            <Plus size={20} />
          </button>
          {onToggle && (
            <button className="toggle-btn">
              {isCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </button>
          )}
        </div>
      </div>

      {!isCollapsed && (
      <div className="floor-list" ref={listRef}>
        {floors.map((floor) => {
          const floorId = String(floor.id);
          const isActive = String(currentFloor) === floorId;
          const isEditing = editingFloorId === floorId;
          const isSaving = savingFloorId === floorId;

          return (
            <div
              key={floor.id}
              className={`floor-item ${isActive ? 'active' : ''} ${isEditing ? 'is-editing' : ''}`}
              onClick={() => {
                if (!isEditing) {
                  onChange(floor.id);
                }
              }}
            >
              <div className="floor-item-main">
                {isEditing ? (
                  <input
                    ref={editInputRef}
                    type="text"
                    value={editingFloorName}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => setEditingFloorName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSaveFloorName();
                      }

                      if (event.key === 'Escape') {
                        event.preventDefault();
                        handleCancelEditFloor();
                      }
                    }}
                    className="floor-item-name-input"
                    placeholder="Nome do ambiente"
                    disabled={isSaving}
                  />
                ) : (
                  <span className="floor-item-name">{floor.name}</span>
                )}
                {isEditing ? (
                  <small className="floor-item-meta">
                    {isSaving ? 'Salvando...' : 'Pressione Enter para salvar ou Esc para cancelar'}
                  </small>
                ) : isFocused && (
                  <small className="floor-item-meta">
                    {isActive ? 'Ambiente ativo' : 'Clique para selecionar'}
                  </small>
                )}
              </div>
              <div className="floor-actions">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className="edit-btn confirm-btn"
                      onClick={(event) => handleSaveFloorName(event)}
                      disabled={isSaving || !editingFloorName.trim()}
                      aria-label="Salvar nome do ambiente"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      type="button"
                      className="edit-btn cancel-btn"
                      onClick={(event) => handleCancelEditFloor(event)}
                      disabled={isSaving}
                      aria-label="Cancelar edição do ambiente"
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="edit-btn"
                      onClick={(event) => handleStartEditFloor(event, floor)}
                      aria-label={`Editar nome do ambiente ${floor.name}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      className="delete-btn"
                      onClick={(event) => handleDeleteFloor(event, floor)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {isAddingFloor && (
          <div className="new-floor-form">
            <input
              type="text"
              value={newFloorName}
              onChange={(e) => setNewFloorName(e.target.value)}
              placeholder="Nome do ambiente"
              autoFocus
            />
            <div className="form-actions">
              <button onClick={handleSaveNewFloor}>Salvar</button>
              <button onClick={handleCancelNewFloor}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
      )}

      {showForceDeleteModal && floorPendingDelete && (
        <div className="floor-delete-modal-overlay" role="dialog" aria-modal="true">
          <div className="floor-delete-modal">
            <div className="floor-delete-modal-header">
              <AlertTriangle size={28} />
              <div>
                <h4>Excluir planta e reservas</h4>
                <p>Esta ação não poderá ser desfeita.</p>
              </div>
            </div>
            <div className="floor-delete-modal-body">
              <p>
                {forceDeleteMessage || 'Existem reservas vinculadas a esta planta.'}
              </p>
              <p className="floor-delete-modal-warning">
                Ao confirmar, todas as reservas associadas a "{floorPendingDelete.name}" serão removidas permanentemente antes da exclusão da planta.
              </p>
            </div>
            <div className="floor-delete-modal-actions">
              <button
                type="button"
                className="modal-button cancel"
                onClick={handleCancelForceDelete}
                disabled={isForceDeleting}
              >
                Manter planta
              </button>
              <button
                type="button"
                className="modal-button delete"
                onClick={handleConfirmForceDelete}
                disabled={isForceDeleting}
              >
                {isForceDeleting ? 'Excluindo...' : 'Excluir tudo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorSelector;
